import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { reportError } from "@/lib/errors";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { EzLogo, NavMobileToggle, NavDesktopLinks, COACHES, CoachCard, CLIENT_TESTIMONIALS } from "@/pages/LandingPage";
import mixpanel from "@/mixpanel";
import {
  ArrowLeft, ArrowRight, Upload, FileText, X,
  Clock, Users, Zap, Heart, Sparkles, CheckCircle2,
  GraduationCap, School, Pencil, MapPin, ShieldCheck, Star, Linkedin, Quote
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut",
  "Delaware","District of Columbia","Florida","Georgia","Hawaii","Idaho","Illinois",
  "Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts",
  "Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada",
  "New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota",
  "Ohio","Oklahoma","Oregon","Pennsylvania","Puerto Rico","Rhode Island",
  "South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia",
  "Washington","West Virginia","Wisconsin","Wyoming",
];

// State-based eligibility is enforced server-side now (low score for restricted
// states); the frontend no longer blocks the survey based on state selection.

const STEPS = [
  {
    id: "hours",
    title: "Are you able to work at least 28 hours per week?",
    subtitle: "Our guides work 30-40 hours on average.",
    icon: <Clock className="w-6 h-6" />,
    pitch: "As a Guide, you'll build real relationships with your clients. Consistent availability lets you provide the continuity seniors need to feel supported.",
    testimonial: { quote: "My clients look forward to our calls. One told me I'm the only person who really listens to her.", name: "Maria R.", role: "Health Guide" },
  },
  {
    id: "contract",
    title: "Are you open to a flexible hourly role?",
    subtitle: "This role is paid hourly and is intended to be an ongoing, long-term opportunity.",
    icon: <Zap className="w-6 h-6" />,
    pitch: "Flexibility is a core perk. Work daytime hours, no weekends, from anywhere. Set your own rhythm while making a real impact.",
    testimonial: { quote: "I drop my kids at school, start my calls at 9, and I'm done by 4. The flexibility is unreal.", name: "Sarah L.", role: "Health Guide, mom of two" },
  },
  {
    id: "seniors",
    title: "Do you love talking to seniors?",
    subtitle: "This role is all about meaningful conversations.",
    icon: <Heart className="w-6 h-6" />,
    pitch: "Our clients aren't just names on a list — they're people who've lived extraordinary lives and just need someone in their corner.",
    testimonial: { quote: "My guide helped me understand my Medicare options in a way no one else could. I finally feel like someone is in my corner.", name: "Judith, 74", role: "Ez Health Client" },
  },
  {
    id: "state",
    title: "What state do you live in?",
    subtitle: "Select the state you currently reside in.",
    icon: <MapPin className="w-6 h-6" />,
    type: "state_dropdown",
    field: "state",
    pitch: "Our guides work from home across the country. We're growing fast and adding new states all the time.",
    testimonial: { quote: "I work from my living room in North Carolina and serve clients across multiple states. The flexibility is amazing.", name: "Priya M.", role: "Health Guide" },
  },
  {
    id: "college",
    title: "What college did you attend?",
    subtitle: "Undergraduate institution name.",
    icon: <School className="w-6 h-6" />,
    pitch: "We hire people from all kinds of backgrounds — community colleges, state schools, Ivy League, online programs. What matters is curiosity and drive.",
    testimonial: { quote: "I went to a small state school and I fit right in. Nobody cares where you went — they care that you show up for your clients.", name: "Sarah L.", role: "Health Guide" },
  },
  {
    id: "gpa",
    title: "What was your undergraduate GPA?",
    subtitle: "We encourage you to share your GPA — it helps us learn about you.",
    icon: <GraduationCap className="w-6 h-6" />,
    pitch: "You'll work alongside smart, motivated people who came from companies like Noom. We value curious minds who love learning — your GPA is just one small signal.",
    testimonial: { quote: "The team here is sharp but incredibly supportive. I learn something new every single day.", name: "Rachel K.", role: "Former Teacher, now Health Guide" },
  },
  {
    id: "score",
    title: "Do you have a standardized test score?",
    subtitle: "SAT or ACT. It's okay to submit an approximate score if you don't remember your exact one.",
    icon: <Sparkles className="w-6 h-6" />,
    pitch: "This helps us understand your analytical background. Don't worry if it's been a while — we know a test score isn't who you are. But sharing one does help your application.",
    testimonial: null,
  },
  {
    id: "resume",
    title: "Upload your resume",
    subtitle: "PDF or DOC format. We'll review it along with your answers.",
    icon: <FileText className="w-6 h-6" />,
    pitch: "We look at the whole person, not just credentials. Health coaches, teachers, customer service pros — all make incredible guides. No healthcare background needed.",
    testimonial: { quote: "I came from teaching — zero healthcare background. The training was incredible and the founders genuinely care about your growth.", name: "Jenna K.", role: "Former High School Teacher" },
  },
  {
    id: "bonus",
    title: "Why do you love working with seniors?",
    subtitle: "Show us your qualifications, passion and quality of writing.",
    icon: <Heart className="w-6 h-6" />,
    pitch: "Your unique life experiences matter. Whether it's a grandparent who inspired you or a career in caregiving — you have a lot to share and give.",
    testimonial: { quote: "I wrote about my grandmother in my application. Six months later, I'm helping dozens of people just like her. It's the most fulfilling work I've ever done.", name: "Priya M.", role: "Health Guide" },
  },
  {
    id: "contact",
    title: "Last step — how can we reach you?",
    subtitle: "We'll use this to follow up on your application.",
    icon: <Users className="w-6 h-6" />,
    pitch: "You're almost there! You're joining a small, mission-driven team helping seniors navigate healthcare. Our founders scaled Noom to $3.4B — now they're building something even more meaningful.",
    testimonial: { quote: "Joining Ez Health was the best career decision I ever made. I finally feel like my work matters.", name: "Maria R.", role: "Former Health Coach, now Health Guide" },
  },
];


// Trust-building info screens inserted into the survey flow (Guide only).
// FLOW is the linear ordering the candidate sees; each "q" entry points at an
// index in STEPS for the underlying question, "info" entries are pure
// interstitials that don't read or write any form field. Question semantics —
// `canProceed`, `getStepAnswer`, mixpanel events, the submit payload — keep
// using the original 0..9 question index (`step`, derived below), so the
// answer-processing flow is completely unaffected.
const FLOW = [
  { kind: "q", q: 0 },
  { kind: "q", q: 1 },
  { kind: "info", id: "bbb" },
  { kind: "q", q: 2 },
  { kind: "q", q: 3 },
  { kind: "info", id: "coaches" },
  { kind: "q", q: 4 },
  { kind: "q", q: 5 },
  { kind: "q", q: 6 },
  { kind: "q", q: 7 },
  { kind: "q", q: 8 },
  { kind: "q", q: 9 },
];
const FLOW_QUESTION_COUNT = FLOW.filter(f => f.kind === "q").length;

const collegeListRef = { current: null };

// Renders the two trust-building interstitials inserted into the survey flow.
// Pure presentational — no form fields, no answer collection, no submit
// interaction. The Next button lives in the survey's main navigation bar and
// just advances `flowPos`. Each variant pulls in real, externally-verifiable
// data (BBB profile, real coach LinkedIns) to reinforce trust at the exact
// moments candidates start wondering whether the role is real.
function TrustInfoScreen({ id }) {
  if (id === "bbb") {
    return (
      <div data-testid={`trust-screen-${id}`}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wider font-body">
            Verified by the BBB
          </span>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-ez-teal font-heading tracking-tight mb-6" data-testid="trust-screen-title">
          Join a team clients truly love
        </h2>

        {/* Single combined trust card: BBB rating + customer review average
            sit side-by-side, separated by a vertical divider. Links to the
            BBB customer-reviews page (where both stats originate). */}
        <a
          href="https://www.bbb.org/us/fl/miami/profile/health-consultants/ez-health-medical-group-pa-0633-92044638/customer-reviews"
          target="_blank"
          rel="noopener noreferrer"
          className="group block bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-lg hover:border-emerald-200 hover:-translate-y-0.5 transition-all"
          data-testid="trust-screen-bbb-combined"
        >
          <div className="flex items-stretch gap-5 sm:gap-7">
            <div className="flex-1 flex items-center gap-3 min-w-0">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600 group-hover:bg-emerald-100 transition-colors">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider font-body">BBB Rating</p>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-3xl font-bold text-ez-teal font-heading tracking-tight leading-none">A</span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" aria-label="Verified" />
                </div>
              </div>
            </div>
            <div className="w-px bg-slate-200" aria-hidden="true" />
            <div className="flex-1 flex items-center gap-3 min-w-0">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-500 group-hover:bg-amber-100 transition-colors">
                <Star className="w-5 h-5 fill-amber-400 stroke-amber-500" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider font-body">Customer reviews</p>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-3xl font-bold text-ez-teal font-heading tracking-tight leading-none">4.99</span>
                  <span className="text-xs text-slate-500 font-body">avg</span>
                </div>
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500 font-body mt-3 group-hover:text-ez-blue transition-colors">
            View on BBB <ArrowRight className="w-3 h-3 inline -mt-0.5 opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-1 transition-all" />
          </p>
        </a>

        {/* Two short real BBB reviews. Same source as the homepage carousel. */}
        <div className="mt-6">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider font-body mb-3">
            What clients are saying
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {CLIENT_TESTIMONIALS.slice(0, 2).map((t) => (
              <div
                key={t.name}
                className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm"
                data-testid={`trust-screen-review-${t.name.toLowerCase()}`}
              >
                <Quote className="w-4 h-4 text-ez-peach/50 mb-2" />
                <p className="text-sm text-slate-700 font-body leading-relaxed mb-3">"{t.quote}"</p>
                <p className="text-xs font-semibold text-ez-teal font-heading">
                  — {t.name}, {t.age}
                  <span className="font-normal text-slate-500"> · {t.location}</span>
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  if (id === "coaches") {
    return (
      <div data-testid={`trust-screen-${id}`}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-ez-blue/10 flex items-center justify-center text-ez-blue">
            <Users className="w-6 h-6" />
          </div>
          <span className="text-xs font-semibold text-ez-blue uppercase tracking-wider font-body">
            From our team
          </span>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-ez-teal font-heading tracking-tight mb-2" data-testid="trust-screen-title">
          Meet your future teammates
        </h2>
        <p className="text-slate-500 font-body mb-8 max-w-xl">
          A few of the coaches you'd be working alongside.
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {COACHES.map((c, i) => (
            <CoachCard key={c.name} coach={c} index={i} />
          ))}
        </div>
      </div>
    );
  }
  return null;
}

function CollegeTypeahead({ value, onChange }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("search"); // "search" | "other" | "none"
  const wrapperRef = useRef(null);

  // Load college list once, shared across instances
  useEffect(() => {
    if (collegeListRef.current) return;
    fetch("/colleges.json")
      .then(r => r.json())
      .then(data => { collegeListRef.current = data; })
      .catch(() => { collegeListRef.current = []; });
  }, []);

  // Initialize mode from existing value
  useEffect(() => {
    if (value === "I didn't attend college") setMode("none");
    else if (value && mode === "search") setQuery(value);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const search = useCallback((q) => {
    if (!q || q.length < 2) { setResults([]); setOpen(false); return; }
    const list = collegeListRef.current || [];
    const ql = q.toLowerCase();
    const prefix = [];
    const substring = [];
    for (const name of list) {
      const nl = name.toLowerCase();
      if (nl.startsWith(ql)) prefix.push(name);
      else if (nl.includes(ql)) substring.push(name);
      if (prefix.length + substring.length >= 24) break;
    }
    const matches = [...prefix, ...substring].slice(0, 12);
    setResults(matches);
    setOpen(matches.length > 0);
  }, []);

  const selectCollege = (name) => {
    setQuery(name);
    onChange(name);
    setOpen(false);
  };

  if (mode === "none") {
    return (
      <div className="mt-6 text-center">
        <p className="text-lg font-heading text-slate-700 mb-3">No college selected</p>
        <button
          data-testid="college-undo-none"
          onClick={() => { setMode("search"); onChange(""); setQuery(""); }}
          className="text-sm text-blue-600 hover:underline font-body"
        >
          Actually, I did attend college
        </button>
      </div>
    );
  }

  if (mode === "other") {
    return (
      <div className="mt-6">
        <Input
          data-testid="input-college-other"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Type your college name..."
          autoFocus
          className="h-14 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-ez-blue focus:ring-ez-blue text-lg font-heading"
        />
        <button
          data-testid="college-back-search"
          onClick={() => { setMode("search"); onChange(""); setQuery(""); }}
          className="text-xs text-blue-600 hover:underline font-body mt-2 block mx-auto"
        >
          Back to search
        </button>
      </div>
    );
  }

  return (
    <div className="mt-6" ref={wrapperRef}>
      <div className="relative">
        <Input
          data-testid="input-college"
          value={query}
          onChange={e => { setQuery(e.target.value); onChange(e.target.value); search(e.target.value); }}
          onFocus={() => { if (results.length) setOpen(true); }}
          placeholder="Start typing your college name..."
          autoComplete="off"
          className="h-14 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-ez-blue focus:ring-ez-blue text-lg font-heading"
        />
        {open && results.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
            {results.map((name, i) => (
              <button
                key={i}
                data-testid={`college-option-${i}`}
                onClick={() => selectCollege(name)}
                className="w-full text-left px-4 py-2.5 text-sm font-body hover:bg-blue-50 transition-colors border-b border-slate-50 last:border-0"
              >
                {name}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center justify-center gap-4 mt-3">
        <button
          data-testid="college-other-btn"
          onClick={() => { setMode("other"); onChange(""); setOpen(false); }}
          className="text-xs text-slate-500 hover:text-blue-600 font-body hover:underline"
        >
          My college isn't listed
        </button>
        <span className="text-slate-300">|</span>
        <button
          data-testid="college-none-btn"
          onClick={() => { setMode("none"); onChange("I didn't attend college"); setOpen(false); }}
          className="text-xs text-slate-500 hover:text-blue-600 font-body hover:underline"
        >
          I didn't attend college
        </button>
      </div>
    </div>
  );
}

export default function ApplicationSurvey() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [flowPos, setFlowPos] = useState(0);
  const flowItem = FLOW[flowPos];
  // `step` keeps the original 0..9 question-index semantics for all answer
  // logic (canProceed, getStepAnswer, switch (step) branches, mixpanel events,
  // submit payload). On info-screen positions we use the LAST question's index
  // as a harmless placeholder — none of the question-only code paths execute
  // for info screens because we branch on flowItem.kind in render and next().
  const step = flowItem.kind === "q" ? flowItem.q : (FLOW[flowPos - 1]?.q ?? 0);
  const setStep = (q) => {
    // Compatibility shim for the pre-fill effect below: jump flow to the first
    // flow position whose underlying question index is `q`.
    const idx = FLOW.findIndex(f => f.kind === "q" && f.q === q);
    if (idx >= 0) setFlowPos(idx);
  };
  const [submitting, setSubmitting] = useState(false);
  // Resume already uploaded via the Indeed flow (no re-upload needed)
  const [indeedResume, setIndeedResume] = useState({ has: false, filename: "" });

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    can_work_25_hours: null,
    state: "",
    college_name: "",
    undergrad_gpa: "",
    gpa_scale: "4.0",
    gpa_unknown: false,
    test_type: null, // "sat", "act", or "none"
    sat_score: "",
    sat_total: "1600",
    act_score: "",
    okay_with_1099: null,
    loves_talking_to_seniors: null,
    resume: null,
    why_love_seniors: "",
  });

  // Pre-fill from landing page first question
  useEffect(() => {
    if (location.state?.can_work_25_hours !== undefined) {
      setForm(prev => ({ ...prev, can_work_25_hours: location.state.can_work_25_hours }));
      setStep(1);
    }
  }, [location.state]);

  // Pre-fill name + phone from Indeed SMS link (?user=<id>)
  const indeedRefId = searchParams.get("user") || "";
  useEffect(() => {
    if (!indeedRefId) return;
    const API = process.env.REACT_APP_BACKEND_URL;
    api.get(`${API}/api/indeed-prefill/${indeedRefId}`)
      .then(r => r.data)
      .then(data => {
        if (!data.found) return;
        const updates = {};
        if (data.name) updates.full_name = data.name;
        if (data.email) updates.email = data.email;
        if (data.phone) {
          // Format E.164 phone (+1XXXXXXXXXX) to (XXX) XXX-XXXX for the form
          const digits = data.phone.replace(/\D/g, "").slice(-10);
          if (digits.length === 10) {
            updates.phone = `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
          } else {
            updates.phone = data.phone;
          }
        }
        if (Object.keys(updates).length) {
          setForm(prev => ({ ...prev, ...updates }));
        }
        setIndeedResume({ has: !!data.has_resume, filename: (data.has_resume && data.resume_filename) || "" });
      })
      .catch(() => {}); // fail silently
  }, [indeedRefId]);

  const [gpaError, setGpaError] = useState("");
  const [stepError, setStepError] = useState("");
  const [satTotalEditing, setSatTotalEditing] = useState(false);

  const totalSteps = STEPS.length;
  // Progress bar reflects position in the visible flow (questions + info
  // screens), so each "Next" click pushes the bar forward — info screens still
  // feel like progress to the candidate.
  const progress = ((flowPos + 1) / FLOW.length) * 100;
  const current = STEPS[step];

  const updateForm = (key, value) => { setStepError(""); setForm(prev => ({ ...prev, [key]: value })); };

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const gpaMax = parseFloat(form.gpa_scale) || 4.0;

  const handleGpaChange = (val) => {
    if (val === "" || /^\d*\.?\d*$/.test(val)) {
      updateForm("undergrad_gpa", val);
      const num = parseFloat(val);
      if (val && (isNaN(num) || num < 0 || num > gpaMax)) {
        setGpaError(`GPA must be between 0.0 and ${gpaMax.toFixed(1)}`);
      } else {
        setGpaError("");
      }
    }
  };

  const handleGpaScaleChange = (scale) => {
    updateForm("gpa_scale", scale);
    // Re-validate current GPA against new scale
    const max = parseFloat(scale);
    const num = parseFloat(form.undergrad_gpa);
    if (form.undergrad_gpa && !isNaN(num) && num > max) {
      setGpaError(`GPA must be between 0.0 and ${max.toFixed(1)}`);
    } else {
      setGpaError("");
    }
  };

  const handleSatField = (field, val) => {
    if (val === "" || /^\d+$/.test(val)) {
      updateForm(field, val);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 0: return form.can_work_25_hours !== null;
      case 1: return form.okay_with_1099 !== null;
      case 2: return form.loves_talking_to_seniors !== null;
      case 3: return !!form.state;
      case 4: return !!form.college_name.trim();
      case 5: {
        if (form.gpa_unknown) return true;
        if (!form.undergrad_gpa) return false;
        const num = parseFloat(form.undergrad_gpa);
        return !isNaN(num) && num >= 0 && num <= gpaMax;
      }
      case 6: return form.test_type !== null;
      case 7: return !!form.resume || indeedResume.has;
      case 8: return !!form.why_love_seniors.trim();
      case 9: return form.full_name.trim() && form.email.trim() && form.phone.trim();
      default: return true;
    }
  };

  const getStepErrorMessage = () => {
    switch (step) {
      case 0: return "Please select Yes or No to continue.";
      case 1: return "Please select Yes or No to continue.";
      case 2: return "Please select Yes or No to continue.";
      case 3: return "Please select a state.";
      case 4: return "Please select a college, enter one manually, or choose \"I didn't attend college\".";
      case 5: return "Please enter your GPA or select \"I don't know my GPA\".";
      case 6: return "Please select SAT, ACT, or \"I didn't take either\".";
      case 7: return "Please upload your resume to continue.";
      case 8: return "Please write a short answer to continue.";
      case 9: return "Please fill in your name, email, and phone number.";
      default: return "Please answer this question to continue.";
    }
  };

  const getStepAnswer = (s) => {
    switch (s) {
      case 0: return form.can_work_25_hours;
      case 1: return form.okay_with_1099;
      case 2: return form.loves_talking_to_seniors;
      case 3: return form.state || null;
      case 4: return form.college_name || null;
      case 5: return form.gpa_unknown ? "unknown" : form.undergrad_gpa || null;
      case 6: return form.test_type === "sat" ? form.sat_score : form.test_type === "act" ? form.act_score : form.test_type || null;
      case 7: return form.resume ? form.resume.name : (indeedResume.has ? "indeed_resume" : null);
      case 8: return form.why_love_seniors || null;
      case 9: return form.email || null;
      default: return null;
    }
  };

  const STEP_EVENTS = ["workHoursCompleted", "contractCompleted", "lovesSeniorsCompleted", "stateCompleted", "collegeCompleted", "gpaCompleted", "testScoreCompleted", "resumeCompleted", "whySeniorsCompleted", "contactCompleted"];

  const next = () => {
    // Info screens just advance — they collect no input and need no validation.
    if (flowItem.kind === "info") {
      setStepError("");
      setFlowPos(p => Math.min(p + 1, FLOW.length - 1));
      return;
    }
    if (!canProceed()) {
      setStepError(getStepErrorMessage());
      return;
    }
    setStepError("");
    if (flowPos < FLOW.length - 1) {
      mixpanel.track(STEP_EVENTS[step], { answer: getStepAnswer(step) });
      setFlowPos(p => p + 1);
    }
  };
  const back = () => {
    setStepError("");
    if (flowPos === 0) {
      navigate("/");
    } else {
      setFlowPos(p => p - 1);
    }
  };

  const handleSubmit = async () => {
    if (!canProceed()) {
      const message = getStepErrorMessage();
      setStepError(message);
      // The contact step has no single answer (name/email/phone), so capture
      // those fields explicitly; `answer` covers the single-field steps.
      mixpanel.track("applicationSubmitValidationFailed", {
        step,
        step_id: current.id,
        message,
        answer: getStepAnswer(step) ?? null,
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
      });
      return;
    }
    setStepError("");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("full_name", form.full_name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("can_work_25_hours", form.can_work_25_hours ? "true" : "false");
      fd.append("college_name", form.college_name);
      fd.append("undergrad_gpa", form.gpa_unknown ? "I don't know my GPA" : form.undergrad_gpa);
      fd.append("gpa_scale", form.gpa_unknown ? "" : form.gpa_scale);
      fd.append("test_type", form.test_type || "");
      let scoreStr = "";
      if (form.test_type === "sat" && form.sat_score && form.sat_total) {
        scoreStr = `SAT: ${form.sat_score} / ${form.sat_total}`;
      } else if (form.test_type === "act" && form.act_score) {
        scoreStr = `ACT: ${form.act_score} / 36`;
      } else if (form.test_type === "none") {
        scoreStr = "No standardized test score";
      }
      fd.append("standardized_score", scoreStr);
      fd.append("okay_with_1099", form.okay_with_1099 ? "true" : "false");
      fd.append("loves_talking_to_seniors", form.loves_talking_to_seniors ? "true" : "false");
      fd.append("why_love_seniors", form.why_love_seniors);
      if (form.state) fd.append("timezone_or_state", form.state);
      if (indeedRefId) fd.append("indeed_ref_id", indeedRefId);
      fd.append("job_type", "guide");
      if (form.resume) fd.append("resume", form.resume);

      const res = await api.post(`${API}/applications`, fd, {
        timeout: 60_000,
        retry: false,
      });
      mixpanel.identify(form.email);
      mixpanel.people.set({ "$email": form.email, "$name": form.full_name, "$phone": form.phone });
      mixpanel.track("contactCompleted", { answer: form.email, name: form.full_name, email: form.email, phone: form.phone });
      mixpanel.track("applicationSubmitted", {
        has_resume: !!form.resume || indeedResume.has,
        has_college: !!form.college_name,
        has_test_score: form.test_type && form.test_type !== "none",
        can_work_25_hours: form.can_work_25_hours,
        okay_with_1099: form.okay_with_1099,
      });
      const appId = res.data?.id;
      navigate("/willo", { state: { appId, scoring: true } });
    } catch (err) {
      const message = err.response?.status === 409
        ? "You've already applied for this position with this email."
        : "Failed to submit application. Please try again.";
      reportError(err, message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    const MAX_RESUME_SIZE = 20 * 1024 * 1024; // 20MB

    if (file) {
      if (file.size > MAX_RESUME_SIZE) {
        toast.error("Resume must be under 20MB");
      } else {
        updateForm("resume", file);
      }
    }
  }, []);

  const slideVariants = {
    enter: { opacity: 0, x: 30 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -30 },
  };

  const YesNoButtons = ({ field }) => (
    <div className="flex gap-4 mt-6">
      <button
        data-testid={`${field}-yes-btn`}
        onClick={() => updateForm(field, true)}
        className={`flex-1 py-5 rounded-2xl text-lg font-semibold font-heading border-2 transition-colors duration-200 ${
          form[field] === true
            ? "bg-ez-blue text-white border-ez-blue shadow-lg shadow-blue-200"
            : "bg-white text-ez-teal border-slate-200 hover:border-ez-blue/50"
        }`}
      >
        Yes
      </button>
      <button
        data-testid={`${field}-no-btn`}
        onClick={() => updateForm(field, false)}
        className={`flex-1 py-5 rounded-2xl text-lg font-semibold font-heading border-2 transition-colors duration-200 ${
          form[field] === false
            ? "bg-slate-600 text-white border-slate-600 shadow-lg"
            : "bg-white text-ez-teal border-slate-200 hover:border-slate-400"
        }`}
      >
        No
      </button>
    </div>
  );

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return <YesNoButtons field="can_work_25_hours" />;
      case 1:
        return <YesNoButtons field="okay_with_1099" />;
      case 2:
        return <YesNoButtons field="loves_talking_to_seniors" />;
      case 3:
        return (
          <div className="mt-6" data-testid="step-state">
            <select
              data-testid="select-state"
              value={form.state}
              onChange={e => updateForm("state", e.target.value)}
              className="w-full h-14 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-ez-teal focus:ring-ez-teal text-base px-4 appearance-none cursor-pointer"
            >
              <option value="">Select a state...</option>
              {US_STATES.map(st => (
                <option key={st} value={st}>{st}</option>
              ))}
            </select>
          </div>
        );
      case 4:
        return <CollegeTypeahead value={form.college_name} onChange={(v) => updateForm("college_name", v)} />;
      case 5:
        return (
          <div className="mt-6 space-y-5">
              <div className={`transition-opacity duration-200 space-y-5 ${form.gpa_unknown ? "opacity-40 pointer-events-none" : ""}`}>
                {/* GPA Scale Selector */}
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-sm text-slate-500 font-body">My GPA is on a</span>
                  {["4.0", "5.0"].map(scale => (
                    <button
                      key={scale}
                      data-testid={`gpa-scale-${scale}`}
                      onClick={() => handleGpaScaleChange(scale)}
                      className={`px-4 py-1.5 rounded-full text-sm font-semibold font-heading border-2 transition-colors duration-150 ${
                        form.gpa_scale === scale
                          ? "bg-ez-blue text-white border-ez-blue"
                          : "bg-white text-ez-teal border-slate-200 hover:border-ez-blue/40"
                      }`}
                    >
                      {scale}
                    </button>
                  ))}
                  <span className="text-sm text-slate-500 font-body">scale</span>
                </div>
                <Input
                  data-testid="input-gpa"
                  value={form.undergrad_gpa}
                  onChange={e => handleGpaChange(e.target.value)}
                  placeholder={`e.g., ${gpaMax === 5.0 ? "4.2" : "3.5"}`}
                  inputMode="decimal"
                  className={`h-14 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-ez-blue focus:ring-ez-blue text-lg text-center font-heading ${gpaError ? 'border-red-300 focus:border-red-400 focus:ring-red-400' : ''}`}
                />
                {gpaError ? (
                  <p className="text-xs text-red-500 text-center font-body" data-testid="gpa-error">{gpaError}</p>
                ) : (
                  <p className="text-xs text-slate-400 text-center font-body">On a {gpaMax.toFixed(1)} scale. Numbers only.</p>
                )}
              </div>
            {/* Opt-out */}
            <button
              data-testid="gpa-unknown-btn"
              onClick={() => {
                updateForm("gpa_unknown", !form.gpa_unknown);
                if (!form.gpa_unknown) {
                  updateForm("undergrad_gpa", "");
                  setGpaError("");
                }
              }}
              className={`w-full py-4 rounded-xl text-sm font-medium font-body border-2 transition-colors duration-200 ${
                form.gpa_unknown
                  ? "bg-slate-100 text-slate-700 border-slate-300"
                  : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
              }`}
            >
              {form.gpa_unknown ? "I don't know my GPA (selected)" : "I don't know my GPA"}
            </button>
            {form.gpa_unknown && (
              <p className="text-xs text-amber-600 text-center font-body mt-1">
                Submitting a GPA significantly strengthens your application.
              </p>
            )}
          </div>
        );
      case 6:
        return (
          <div className="mt-6 space-y-4">
            {/* Test type selector */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { key: "sat", label: "SAT" },
                { key: "act", label: "ACT" },
                { key: "none", label: "I didn't take either" },
              ].map(opt => (
                <button
                  key={opt.key}
                  data-testid={`test-type-${opt.key}`}
                  onClick={() => updateForm("test_type", opt.key)}
                  className={`py-4 rounded-2xl font-semibold font-heading border-2 transition-colors duration-200 ${
                    form.test_type === opt.key
                      ? opt.key === "none"
                        ? "bg-slate-100 text-slate-700 border-slate-300"
                        : "bg-ez-blue text-white border-ez-blue shadow-lg shadow-blue-200"
                      : "bg-white text-ez-teal border-slate-200 hover:border-ez-blue/40"
                  } ${opt.key === "none" ? "text-sm" : "text-lg"}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* SAT fields */}
            {form.test_type === "sat" && (
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-slate-600 mb-1.5 block font-body">Your Score</label>
                    <Input
                      data-testid="input-sat-score"
                      value={form.sat_score}
                      onChange={e => handleSatField("sat_score", e.target.value)}
                      placeholder="1300"
                      inputMode="numeric"
                      className="h-14 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-ez-blue focus:ring-ez-blue text-lg text-center font-heading"
                    />
                  </div>
                  <span className="text-2xl text-slate-300 font-heading mt-6">out of</span>
                  <div className="flex-1">
                    <label className="text-sm font-medium text-slate-600 mb-1.5 block font-body">Total Possible</label>
                    {satTotalEditing ? (
                      <Input
                        data-testid="input-sat-total"
                        value={form.sat_total}
                        onChange={e => handleSatField("sat_total", e.target.value)}
                        placeholder="1600"
                        inputMode="numeric"
                        autoFocus
                        onBlur={() => { if (form.sat_total) setSatTotalEditing(false); }}
                        className="h-14 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-ez-blue focus:ring-ez-blue text-lg text-center font-heading"
                      />
                    ) : (
                      <div
                        data-testid="sat-total-display"
                        className="h-14 rounded-xl bg-slate-100 flex items-center justify-center text-lg font-heading text-slate-600 relative cursor-default"
                      >
                        {form.sat_total || "1600"}
                        <button
                          data-testid="sat-total-edit-btn"
                          onClick={() => setSatTotalEditing(true)}
                          className="absolute right-3 w-7 h-7 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-ez-blue hover:border-ez-blue transition-colors duration-200"
                          aria-label="Edit total"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-400 text-center font-body">Numbers only. Most SATs are out of 1600.</p>
              </div>
            )}

            {/* ACT fields */}
            {form.test_type === "act" && (
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-slate-600 mb-1.5 block font-body">Your Composite Score</label>
                    <Input
                      data-testid="input-act-score"
                      value={form.act_score}
                      onChange={e => handleSatField("act_score", e.target.value)}
                      placeholder="28"
                      inputMode="numeric"
                      className="h-14 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-ez-blue focus:ring-ez-blue text-lg text-center font-heading"
                    />
                  </div>
                  <span className="text-2xl text-slate-300 font-heading mt-6">out of</span>
                  <div className="flex-1 flex items-end">
                    <div className="w-full h-14 rounded-xl bg-slate-100 flex items-center justify-center text-lg font-heading text-slate-500 mt-7">
                      36
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-400 text-center font-body">Numbers only. ACT composite is out of 36.</p>
              </div>
            )}

            {/* None selected */}
            {form.test_type === "none" && (
              <p className="text-xs text-amber-600 text-center font-body pt-1">
                Submitting a test score significantly strengthens your application.
              </p>
            )}
          </div>
        );
      case 7: {
        const hasIndeedResume = indeedResume.has && !form.resume;
        return (
          <div className="mt-6">
            <div
              data-testid={hasIndeedResume ? "resume-from-indeed" : "resume-drop-zone"}
              className={`drop-zone ${form.resume || hasIndeedResume ? 'has-file' : ''}`}
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              onClick={() => document.getElementById('resume-input')?.click()}
            >
              {form.resume ? (
                <div className="flex flex-col items-center gap-3">
                  <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                  <p className="font-semibold text-ez-teal font-heading">{form.resume.name}</p>
                  <p className="text-sm text-slate-500 font-body">{(form.resume.size / 1024).toFixed(0)} KB</p>
                  <button
                    data-testid="remove-resume-btn"
                    onClick={e => { e.stopPropagation(); updateForm("resume", null); }}
                    className="text-sm text-red-500 hover:text-red-600 flex items-center gap-1 mt-1"
                  >
                    <X className="w-3 h-3" /> Remove
                  </button>
                </div>
              ) : hasIndeedResume ? (
                <div className="flex flex-col items-center gap-3">
                  <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                  <p className="font-semibold text-ez-teal font-heading">Your Indeed resume is already attached</p>
                  {indeedResume.filename && (
                    <p className="text-sm text-slate-500 font-body">{indeedResume.filename}</p>
                  )}
                  <p
                    data-testid="replace-indeed-resume-btn"
                    className="text-sm text-ez-blue hover:underline mt-1 font-body"
                  >
                    Click or drop a file to replace it
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <Upload className="w-10 h-10 text-ez-blue" />
                  <p className="font-semibold text-ez-teal font-heading">Drop your resume here</p>
                  <p className="text-sm text-slate-500 font-body">or click to browse — PDF, DOC, DOCX</p>
                </div>
              )}
            </div>
            <input
              id="resume-input"
              data-testid="resume-file-input"
              type="file"
              accept=".pdf,.doc,.docx"
              className="hidden"
              onChange={e => {
                const f = e.target.files?.[0];
                if (f && f.size > 20 * 1024 * 1024) {
                  toast.error("Resume must be under 20MB");
                  e.target.value = "";
                } else if (f) {
                  updateForm("resume", f);
                }
              }}
            />
          </div>
        );
      }
      case 8:
        return (
          <div className="mt-6">
            <Textarea
              data-testid="input-why-love-seniors"
              value={form.why_love_seniors}
              onChange={e => updateForm("why_love_seniors", e.target.value)}
              placeholder="Tell us about a moment, a person, or an experience that made you passionate about helping older adults..."
              className="min-h-[140px] rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-ez-blue focus:ring-ez-blue text-base resize-none"
              rows={5}
            />
            <p className="text-xs text-slate-400 mt-2 font-body">We love reading these.</p>
          </div>
        );
      case 9:
        return (
          <div className="space-y-5">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block font-body">Full Name</label>
              <Input
                data-testid="input-full-name"
                value={form.full_name}
                onChange={e => updateForm("full_name", e.target.value)}
                placeholder="Jane Smith"
                className="h-12 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-ez-blue focus:ring-ez-blue text-base"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block font-body">Email</label>
              <Input
                data-testid="input-email"
                type="email"
                value={form.email}
                onChange={e => updateForm("email", e.target.value)}
                placeholder="jane@example.com"
                className="h-12 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-ez-blue focus:ring-ez-blue text-base"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block font-body">Phone</label>
              <Input
                data-testid="input-phone"
                type="tel"
                value={form.phone}
                onChange={e => updateForm("phone", formatPhone(e.target.value))}
                placeholder="(555) 123-4567"
                className="h-12 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:border-ez-blue focus:ring-ez-blue text-base"
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const isLastStep = flowPos === FLOW.length - 1;

  return (
    <div className="min-h-screen bg-white" data-testid="application-survey-page">
      {/* Top Bar */}
      <div className="sticky top-0 z-50 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-3 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-6">
            <NavMobileToggle />
            <EzLogo size="h-6" />
            <NavDesktopLinks />
          </div>
          <span className="text-sm text-slate-500 font-body" data-testid="step-counter">
            Step {flowPos + 1} of {FLOW.length}
          </span>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-4">
          <Progress value={progress} className="h-2.5 rounded-full bg-slate-100" data-testid="survey-progress" />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-12 lg:py-16">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Main Content */}
          <div className={flowItem.kind === "info" ? "lg:col-span-5" : "lg:col-span-3"}>
            <AnimatePresence mode="wait">
              <motion.div
                key={`flow-${flowPos}`}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.25 }}
              >
                {flowItem.kind === "info" ? (
                  <TrustInfoScreen id={flowItem.id} />
                ) : (
                  <>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-ez-blue/10 flex items-center justify-center text-ez-blue">
                        {current.icon}
                      </div>
                      <span className="text-xs font-semibold text-ez-blue uppercase tracking-wider font-body">
                        {current.id === "contact" ? "Final Step" : `Question ${step + 1}`}
                      </span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-ez-teal font-heading tracking-tight mb-2" data-testid="step-title">
                      {current.title}
                    </h2>
                    <p className="text-slate-500 font-body mb-2" data-testid="step-subtitle">{current.subtitle}</p>

                    {renderStepContent()}
                  </>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            {stepError && (
              <div data-testid="step-error" className="mt-8 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-50 border border-red-100">
                <div className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                <p className="text-sm text-red-600 font-body">{stepError}</p>
              </div>
            )}
            <div className={`flex items-center justify-between ${stepError ? "mt-4" : "mt-10"} pt-8 border-t border-slate-100`}>
              <Button
                data-testid="back-btn"
                variant="ghost"
                onClick={back}
                disabled={false}
                className="text-slate-600 hover:text-ez-teal rounded-xl font-body"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>

              {isLastStep ? (
                <Button
                  data-testid="submit-btn"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="rounded-full bg-ez-blue text-white hover:bg-[#5A9BCF] px-8 py-3 font-semibold shadow-lg shadow-blue-200 hover:-translate-y-0.5 active:translate-y-0 transition-transform"
                >
                  {submitting ? "Submitting..." : "Submit Application"}
                </Button>
              ) : (
                <Button
                  data-testid="next-btn"
                  onClick={next}
                  className="rounded-full bg-ez-blue text-white hover:bg-[#5A9BCF] px-8 py-3 font-semibold shadow-lg shadow-blue-200 hover:-translate-y-0.5 active:translate-y-0 transition-transform disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:shadow-none"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>

          {/* Pitch Sidebar — only shown on question screens (info interstitials
              fill the main column on their own with a full-width visual). */}
          {flowItem.kind === "q" && (
            <div className="lg:col-span-2 hidden lg:block">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`pitch-${flowPos}`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                  className="sticky top-32"
                >
                  <div className="bg-ez-surface rounded-2xl border border-slate-100 p-8" data-testid="pitch-panel">
                    <div className="w-10 h-10 rounded-xl bg-ez-peach/20 flex items-center justify-center text-ez-peach mb-4">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <p className="text-slate-700 leading-relaxed font-body">
                      {current.pitch}
                    </p>
                    {current.testimonial && (
                      <div className="mt-6 pt-6 border-t border-slate-200">
                        <p className="text-slate-600 font-body italic text-sm leading-relaxed mb-3">
                          "{current.testimonial.quote}"
                        </p>
                        <p className="text-xs font-semibold text-ez-teal font-heading">
                          — {current.testimonial.name}, <span className="font-normal text-slate-500">{current.testimonial.role}</span>
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
